import React, { useState, useEffect } from 'react';
import { FaHistory, FaEye, FaPlay, FaCalendar, FaClock, FaCheckCircle, FaLightbulb, FaChartLine } from 'react-icons/fa';
import { useMarketingChain } from '../../../hooks/useMarketingChain';

const HistoryList = ({ userId, onSelectProposal, onContinueProposal }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMarca, setSelectedMarca] = useState('all');
    const [marcas, setMarcas] = useState([]);

    const { getHistory, getHistoryByMarca } = useMarketingChain(userId);

    const loadHistory = async () => {
        try {
            setLoading(true);
            let response;

            if (selectedMarca === 'all') {
                response = await getHistory();
            } else {
                response = await getHistoryByMarca(selectedMarca);
            }

            setHistory(response || []);
        } catch (error) {
            console.error('Error cargando historial:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMarcas = async () => {
        try {
            const response = await fetch('/api/marcas');
            const marcasData = await response.json();
            setMarcas(marcasData || []);
        } catch (error) {
            console.error('Error cargando marcas:', error);
        }
    };

    useEffect(() => {
        loadHistory();
        loadMarcas();
    }, [userId, selectedMarca]); // eslint-disable-line react-hooks/exhaustive-deps

    const getStatusBadge = (status) => {
        const statusConfig = {
            'objectives_ready': {
                color: 'bg-blue-100 text-blue-800',
                text: 'Objetivos Listos',
                icon: FaLightbulb
            },
            'strategy_ready': {
                color: 'bg-orange-100 text-orange-800',
                text: 'Estrategias Listas',
                icon: FaChartLine
            },
            'tactics_ready': {
                color: 'bg-green-100 text-green-800',
                text: 'Tácticas Listas',
                icon: FaCheckCircle
            },
            'completed': {
                color: 'bg-purple-100 text-purple-800',
                text: 'Completado',
                icon: FaCheckCircle
            }
        };

        return statusConfig[status] || {
            color: 'bg-gray-100 text-gray-800',
            text: status,
            icon: FaClock
        };
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatExecutionTime = (ms) => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);

        if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
        }
        return `${seconds}s`;
    };

    const isSessionRecent = (sessionDate, maxDays = 7) => {
        const sessionTime = new Date(sessionDate).getTime();
        const currentTime = new Date().getTime();
        const daysDiff = (currentTime - sessionTime) / (1000 * 60 * 60 * 24);
        return daysDiff <= maxDays;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando historial...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <FaHistory className="text-purple-500 text-2xl mr-3" />
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Historial de Propuestas</h2>
                        <p className="text-gray-600">Todas tus propuestas de marketing generadas</p>
                    </div>
                </div>

                {/* Filter by Marca */}
                {marcas.length > 0 && (
                    <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium text-gray-700">Filtrar por marca:</label>
                        <select
                            value={selectedMarca}
                            onChange={(e) => setSelectedMarca(e.target.value)}
                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                            <option value="all">Todas las marcas</option>
                            {marcas.map(marca => (
                                <option key={marca._id} value={marca._id}>
                                    {marca.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* History List */}
            {history.length === 0 ? (
                <div className="text-center py-12">
                    <FaHistory className="text-gray-300 text-6xl mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hay propuestas aún</h3>
                    <p className="text-gray-600">Crea tu primera propuesta de marketing para verla aquí</p>
                </div>
            ) : (
                <div className="grid gap-6">
                    {history.map((proposal) => {
                        const statusInfo = getStatusBadge(proposal.status);
                        const StatusIcon = statusInfo.icon;
                        const isRecent = isSessionRecent(proposal.createdAt);

                        return (
                            <div key={proposal.sessionId} className="bg-white rounded-lg shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                                <div className="p-6">
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center space-x-3">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                                                <StatusIcon className="inline w-3 h-3 mr-1" />
                                                {statusInfo.text}
                                            </span>
                                            {isRecent && (
                                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                                    Reciente
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm text-gray-500">
                                                <FaCalendar className="inline w-3 h-3 mr-1" />
                                                {formatDate(proposal.createdAt)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="space-y-3">
                                        <div>
                                            <h3 className="font-semibold text-gray-900 mb-1">
                                                Objetivo: {proposal.generalObjective}
                                            </h3>
                                            {proposal.selectedObjective && (
                                                <p className="text-sm text-gray-600">
                                                    <strong>Objetivo seleccionado:</strong> {proposal.selectedObjective.title}
                                                </p>
                                            )}
                                            {proposal.selectedStrategy && (
                                                <p className="text-sm text-gray-600">
                                                    <strong>Estrategia seleccionada:</strong> {proposal.selectedStrategy.title}
                                                </p>
                                            )}
                                        </div>

                                        {/* Stats */}
                                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                                            <div className="flex items-center">
                                                <FaClock className="w-3 h-3 mr-1" />
                                                {formatExecutionTime(proposal.executionTime)}
                                            </div>
                                            {proposal.tacticsCount && (
                                                <div className="flex items-center">
                                                    <FaCheckCircle className="w-3 h-3 mr-1" />
                                                    {proposal.tacticsCount} tácticas
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center space-x-3 mt-4 pt-4 border-t border-gray-200">
                                        <button
                                            onClick={() => window.open(`/marketing-chain/strategy/${proposal.sessionId}`, '_blank')}
                                            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                        >
                                            <FaEye className="w-3 h-3 mr-1" />
                                            Ver Detalles
                                        </button>

                                        {isRecent && ['objectives_ready', 'strategy_ready', 'tactics_ready'].includes(proposal.status) && (
                                            <button
                                                onClick={() => onContinueProposal(proposal)}
                                                className="flex items-center px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                                            >
                                                <FaPlay className="w-3 h-3 mr-1" />
                                                Continuar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default HistoryList; 